<template>
  <section class="Page" :style="{ height: pageHeight }">
    <Header class="Page__header" :showServiceMenu="false" />
    <main ref="mainRef" class="Page__main">
      <GlobalNavigation
        class="Page__globalNavigation"
        :globalMenuItems="globalMenuItems"
      />
      <ScrollBox class="Page__scrollBox" always>
        <div ref="groupDivRef" class="Page__groupDiv">
          <section class="Page__groupSection">
            <Breadcrumb class="Page__breadcrumb" :breadcrumb="breadcrumb" />
            <Heading
              type="secondary"
              level="2"
              :heading="messages.heading.groupManagement"
              class="Page__heading"
            />
            <GroupList />
          </section>
        </div>
      </ScrollBox>
    </main>
  </section>
</template>

<script lang="ts">
export default {
  name: 'GroupManagement',
};
</script>

<script lang="ts" setup>
import Header from '@spectee/components/Organisms/Header/index.vue';
import Breadcrumb from '@spectee/components/Molecules/Breadcrumb/index.vue';
import ScrollBox from '@spectee/components/Atoms/ScrollBox/index.vue';
import GlobalNavigation from '@spectee/components/Organisms/GlobalNavigation/index.vue';
import { globalMenuItems } from '../../data/SideMenuItems';
import Heading from '@spectee/components/Atoms/Heading/index.vue';
import GroupList from '@spectee/components/Organisms/GroupList/index.vue';
import { useWindowHeight } from '@spectee/composables/useWindowHeight';
import { ref, onMounted, onUnmounted, nextTick } from 'vue';
import messages from '../../messages.json';

const { pageHeight } = useWindowHeight();

const mainRef = ref<HTMLElement | null>(null);
const groupDivRef = ref<HTMLElement | null>(null);

const breadcrumb = [
  { link: '/', text: messages.heading.home },
  { text: messages.heading.groupManagement },
];

const setContentsHeight = async () => {
  if (mainRef.value && groupDivRef.value) {
    groupDivRef.value.style.height = '';

    await nextTick();

    const mainStyle = getComputedStyle(mainRef.value);
    const mainHeight = parseFloat(mainStyle.height);
    const headerHeight = 24;
    const groupContentHeight = mainHeight - headerHeight;

    const groupTableVisibleHeight = 520;
    const height =
      groupContentHeight > groupTableVisibleHeight
        ? groupContentHeight
        : groupTableVisibleHeight;
    groupDivRef.value.style.height = `${height}px`;
  }
};

onMounted(() => {
  setContentsHeight();
  window.addEventListener('resize', setContentsHeight);
});

onUnmounted(() => {
  window.removeEventListener('resize', setContentsHeight);
});
</script>

<style lang="scss" scoped>
@use '@spectee/assets/scss/_pageForward.scss' as *;

.Page {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: 100vh;
  min-height: 0;
  min-width: 0;

  & &__header {
    position: unset;
  }
  & &__main {
    position: relative;
    display: flex;
    height: 100%;
    overflow: hidden;
    padding: 0;
    flex-grow: 1;
  }

  & &__globalNavigation {
    position: unset;
  }

  &__scrollBox {
    flex: 1;
    overflow: hidden;
  }

  &__groupDiv {
    flex: 1;
    overflow: hidden;
    min-width: $service-min-width;
  }

  &__groupSection {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  &__breadcrumb,
  &__heading {
    padding: $service-unit * 4 $service-unit * 6 0;
  }

  @media (max-width: 767px) {
    & &__globalNavigation {
      position: fixed;
      top: $service-unit * 12;
      left: 0;
      z-index: $service-z-index-f;
    }
    &__breadcrumb,
    &__heading {
      font-size: $service-font-size-mf;
      padding: $service-unit * 4 $service-unit * 12 0;
    }
    &__scrollBox {
      flex: 1;
    }
  }
}
</style>
